import type { AssignmentFolder, AssignmentFolderAction } from "@classroom/domain";
import { useState } from "react";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";

export default function AssignmentFolderDialog({
  folder,
  count,
  onSave,
  onClose,
  fallbackFocus,
}: {
  folder?: AssignmentFolder;
  count: number;
  onSave: (action: AssignmentFolderAction) => Promise<unknown>;
  onClose: () => void;
  fallbackFocus: () => HTMLElement | null;
}) {
  const { t } = useTranslation("assignmentRecords");
  const [name, setName] = useState(folder?.name || "");
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const close = () => {
    if (!saving) onClose();
  };
  const ref = useDialogFocus(true, close, fallbackFocus);
  const title = deleting ? t("folders.deleteTitle") : folder ? t("folders.manageTitle") : t("folders.create");
  async function save(action: AssignmentFolderAction) {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await onSave(action);
      onClose();
    } catch {
      setError(t("folders.failed"));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="assignment-folder-overlay">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="paper-card assignment-folder-dialog"
        tabIndex={-1}
      >
        <h3>{title}</h3>
        {deleting && folder ? (
          <>
            <p>{t("folders.deleteMessage", { name: folder.name, count })}</p>
            {error && (
              <p role="alert" className="form-error">
                {error}
              </p>
            )}
            <div className="assignment-folder-dialog-actions">
              <button type="button" disabled={saving} onClick={() => setDeleting(false)}>
                {t("folders.cancel")}
              </button>
              <button
                type="button"
                className="danger-action"
                disabled={saving}
                onClick={() => save({ type: "delete", id: folder.id })}
              >
                {saving ? t("folders.saving") : t("folders.confirmDelete")}
              </button>
            </div>
          </>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              save(
                folder
                  ? { type: "rename", id: folder.id, name }
                  : { type: "create", folder: { id: crypto.randomUUID(), name } },
              );
            }}
          >
            <label>
              {t("folders.name")}
              <input
                required
                maxLength={50}
                value={name}
                onChange={(event) => setName(event.target.value)}
                data-dialog-initial-focus
              />
            </label>
            {error && (
              <p role="alert" className="form-error">
                {error}
              </p>
            )}
            <div className="assignment-folder-dialog-actions">
              {folder && (
                <button
                  type="button"
                  className="assignment-folder-delete"
                  disabled={saving}
                  onClick={() => setDeleting(true)}
                >
                  {t("folders.delete")}
                </button>
              )}
              <button type="button" disabled={saving} onClick={close}>
                {t("folders.cancel")}
              </button>
              <button className="primary-action" disabled={saving || !name.trim()}>
                {saving ? t("folders.saving") : folder ? t("folders.saveName") : t("folders.create")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
